import BaseController from "../../controller/BaseController";
import AdminDataService from "../../util/AdminDataService";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Button$PressEvent } from "sap/m/Button";
import { Dialog$AfterCloseEvent } from "sap/m/Dialog";
import SelectDialog from "sap/m/SelectDialog";
import StandardListItem from "sap/m/StandardListItem";
import DataService from "../../util/DataService";
import Validator from "../../util/Validator";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import Sorter from "sap/ui/model/Sorter";

/**
 * @namespace com.reply.leaviosa.controller
 */
export default class ProjectsController extends BaseController {
    private readonly DIALOG_ID = "DialogCreateUpdateProject";
    private _oSelectProjectLeadDialog: SelectDialog;
    private _oSelectProjefctUserDialog: SelectDialog;

    public async onInit(): Promise<void> {
        AdminDataService.getProjects();
        DataService.getUsers();
    }

    //*************************************************************************************
    // EVENT HANDLERS
    //*************************************************************************************

    public async onCreateProject(): Promise<void> {
        this.getView().setModel(new JSONModel({
            "edit": false
        }), "project");
        await this.openDialog(this.DIALOG_ID);
    }

    public async onEdit(oEvent: Button$PressEvent): Promise<void> {
        const selectedProject = oEvent.getSource().getBindingContext("dataModel").getObject();
        const project = {
            "edit": true,
            ...selectedProject
        }
        this.getView().setModel(new JSONModel(project), "project");
        await this.openDialog(this.DIALOG_ID);
    }

    public async onSaveDialog(oEvent: Dialog$AfterCloseEvent): Promise<void> {

        const validator = new Validator();
        const valid = validator.validate(oEvent.getSource().getParent());
        if (!valid) return;

        const projectData = (this.getView().getModel("project") as JSONModel).getData();

        if (projectData.id) {
            await AdminDataService.updateProject(projectData);

        } else {
            await AdminDataService.createProject(projectData);
        }

        AdminDataService.getProjects();

        this.destroyDialog(this.DIALOG_ID);
    }

    onCloseDialog(oEvent: Dialog$AfterCloseEvent): void {
        AdminDataService.getProjects();
        this.destroyDialog(this.DIALOG_ID);
    }

    onNavigateproject(oEvent: Button$PressEvent): void {
        const currentProject = oEvent.getSource().getBindingContext("dataModel").getObject() as any;
        this.navTo("project", {
            id: currentProject.id
        });
    }

    async onAddProjectLead(oEvent: Button$PressEvent): Promise<void> {

        AdminDataService.getUsersInGroup("PROJECTLEADS");

        if (!this._oSelectProjectLeadDialog) {
            this._oSelectProjectLeadDialog = new SelectDialog({
                title: "Select Project Lead",
                multiSelect: false,
                items: {
                    path: "dataModel>/PROJECTLEADS",
                    template: new StandardListItem({
                        title: "{dataModel>firstName} {dataModel>lastName}",
                        description: "{dataModel>email}"
                    })
                },
                confirm: this.onProjectLeadSelected.bind(this),
                search: this.onSearchProjectLead.bind(this)
            });

            this.getView().addDependent(this._oSelectProjectLeadDialog);
        }

        this._oSelectProjectLeadDialog.open("");
    }

    private onSearchProjectLead(oEvent: any): void {
        const sQuery = oEvent.getParameter("value");
        const aFilters = [];

        if (sQuery && sQuery.length > 0) {
            aFilters.push(new Filter({
                filters: [
                    new Filter("firstName", FilterOperator.Contains, sQuery),
                    new Filter("lastName", FilterOperator.Contains, sQuery),
                    new Filter("email", FilterOperator.Contains, sQuery)
                ],
                and: false
            }));
        }

        const oBinding = this._oSelectProjectLeadDialog.getBinding("items");
        oBinding.filter(aFilters);
    }

    private async onProjectLeadSelected(oEvent: any): Promise<void> {
        const selectedItem = oEvent.getParameter("selectedItem");
        const projectModel = this.getView().getModel("project") as JSONModel;
        const projectData = projectModel.getData();
        const selectedUser = selectedItem.getBindingContext("dataModel").getObject();
        let response;

        if (selectedItem) {
            response = await this.getClient().models.ProjectLead.create({
                userId: selectedUser.userId,
                projectId: projectData.id
            })
        }

        const newProjectLead = {
            id: response.data.id,
            userId: selectedUser.userId,
            user: {
                id: selectedUser.userId,
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
                email: selectedUser.email
            }
        };
        projectData.leads.push(newProjectLead);
        projectModel.setData(projectData);

    }

    private async onRemoveProjectLead(oEvent: Button$PressEvent): Promise<void> {
        const projectLead = oEvent.getSource().getBindingContext("project").getObject();
        const projectModel = this.getView().getModel("project") as JSONModel;
        const projectData = projectModel.getData();

        projectData.leads = projectData.leads.filter((lead: any) => lead.id !== projectLead.id);
        projectModel.setData(projectData);

        const { data, errors } = await this.getClient().models.ProjectLead.delete({
            id: projectLead.id
        })
    }


    //*****************************************************
    // Project Member
    //*****************************************************

    async onAddProjectUser(oEvent: Button$PressEvent): Promise<void> {

        if (!this._oSelectProjefctUserDialog) {
            this._oSelectProjefctUserDialog = new SelectDialog({
                title: "Select Project User",
                multiSelect: false,
                items: {
                    path: "dataModel>/users",
                    template: new StandardListItem({
                        title: "{dataModel>firstName} {dataModel>lastName}",
                        description: "{dataModel>email}"
                    })
                },
                confirm: this.onProjectUserSelected.bind(this),
                search: this.onSearchProjectUser.bind(this)
            });

            this.getView().addDependent(this._oSelectProjefctUserDialog);
        }

        this._oSelectProjefctUserDialog.open("");
    }

    private onSearchProjectUser(oEvent: any): void {
        const sQuery = oEvent.getParameter("value");
        const aFilters = [];

        if (sQuery && sQuery.length > 0) {
            aFilters.push(new Filter({
                filters: [
                    new Filter("firstName", FilterOperator.Contains, sQuery),
                    new Filter("lastName", FilterOperator.Contains, sQuery),
                    new Filter("email", FilterOperator.Contains, sQuery)
                ],
                and: false
            }));
        }

        const oBinding = this._oSelectProjefctUserDialog.getBinding("items");
        oBinding.filter(aFilters);
    }

    private async onProjectUserSelected(oEvent: any): Promise<void> {
        const selectedItem = oEvent.getParameter("selectedItem");
        const projectModel = this.getView().getModel("project") as JSONModel;
        const projectData = projectModel.getData();
        const selectedUser = selectedItem.getBindingContext("dataModel").getObject();
        let response;

        if (selectedItem) {
            response = await this.getClient().models.ProjectUser.create({
                userId: selectedUser.userId,
                projectId: projectData.id
            })
        }

        const newProjectUser = {
            id: response.data.id,
            userId: selectedUser.userId,
            user: {
                id: selectedUser.userId,
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
                email: selectedUser.email
            }
        };

        projectData.users.push(newProjectUser);
        projectModel.setData(projectData);

    }

    private async onRemoveProjectUser(oEvent: Button$PressEvent): Promise<void> {
        const projectUser = oEvent.getSource().getBindingContext("project").getObject();
        const projectModel = this.getView().getModel("project") as JSONModel;
        const projectData = projectModel.getData();

        projectData.users = projectData.users.filter((user: any) => user.id !== projectUser.id);
        projectModel.setData(projectData);

        const { data, errors } = await this.getClient().models.ProjectUser.delete({
            id: projectUser.id
        })
    }

}